import { ApiClientError } from '@contract-analyzer/api-client';
import { useCallback, useMemo, useState } from 'react';

import { QuotaBlockDialog } from '@/components/quota-block-dialog';
import {
  QuotaBlockCtx,
  type QuotaBlockContextValue,
} from '@/hooks/quota-block-context';

type QuotaResource = 'documents' | 'analysis runs';

const QUOTA_CODE_TO_RESOURCE: Readonly<Record<string, QuotaResource>> = {
  'tenant-document-quota-exceeded': 'documents',
  'tenant-analysis-run-quota-exceeded': 'analysis runs',
};

export function QuotaBlockProvider({ children }: { readonly children: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  const [resource, setResource] = useState<QuotaResource>('documents');

  const openQuotaBlock = useCallback((next: QuotaResource) => {
    setResource(next);
    setOpen(true);
  }, []);

  const handleQuotaError = useCallback(
    (error: unknown): boolean => {
      if (!(error instanceof ApiClientError) || error.status !== 402) {
        return false;
      }
      if (error.code === null) return false;
      const mapped = QUOTA_CODE_TO_RESOURCE[error.code];
      if (mapped === undefined) return false;
      setResource(mapped);
      setOpen(true);
      return true;
    },
    [],
  );

  const value = useMemo<QuotaBlockContextValue>(
    () => ({ handleQuotaError, openQuotaBlock }),
    [handleQuotaError, openQuotaBlock],
  );

  return (
    <QuotaBlockCtx value={value}>
      {children}
      <QuotaBlockDialog
        onOpenChange={setOpen}
        open={open}
        resourceName={resource}
      />
    </QuotaBlockCtx>
  );
}
